// элементы выбора
import React, { FC, useEffect, useRef, useState } from 'react';
import { useTheme } from 'styled-components';
import { CSpan, FInter, FlexBox, useIsMobile } from '@brg/ui';
import { SCROLL_SELECT_ITEM_DELAY, SCROLL_SELECT_ITEM_HEIGHT } from './config';
import {
    Arrow,
    ChooserBox,
    ChooserBoxPart,
    ChooserContainer,
    ChooserNode,
    ChooserOpacity,
    ChooserOpacityBottom,
    Container,
    PrettyPoint,
} from './styles';
import { useModal } from '../../../core';
import { useContextLinksNew } from "../../components/navigationSystem/context/useContextLinksNew";
import { Link } from 'shared/components/link/Link';

/**
 * Контейнер с выбором элемента
 * @param props
 * @constructor
 */
export const SelectContextModalNew: FC<{ visible?: boolean, orientation?: string }> = ({ visible, orientation = 'center' }) => {
    const [scrollingAnimation, setScrollingAnimation] = useState<any>(null);
    const [projectIndex, setProjectIndex] = useState(0);
    const theme = useTheme();
    const modal = useModal();
    const isMobile = useIsMobile();
    const { links, modalSettings: { title, titleColor } } = useContextLinksNew()

    // реф для автоскролла
    const scrollRef = useRef<any>(null);

    // Как только все отрисовалось, надо перевести скролл "куда надо"
    useEffect(() => {
        if (scrollRef.current) {
            const ind = links.findIndex(link => link?.active)
            const scroller = scrollRef.current as HTMLDivElement;
            scroller.scrollTo({ top: ind * SCROLL_SELECT_ITEM_HEIGHT });

            setTimeout(() => {
                setScrollingAnimation(false)
            }, 100)
        }
    }, [links, scrollRef]);

    /**
     * Событие нажатия на стрелочку
     */
    const onArrowClick = (e: React.MouseEvent<HTMLDivElement>, p?: number): void => {
        e.preventDefault();
        e.stopPropagation();
        modal.hide();
        // @ts-ignore
        links[p ?? projectIndex]?.action && links[p ?? projectIndex]?.action()
    };

    /**
     * Фабрика с обработчиком событий
     * @param index
     */
    const onSomeItemClick = (index: number) => {
        /**
         * Обработчик события выбора элемента в списке
         */
        return (ev: React.MouseEvent<HTMLDivElement>): void => {
            ev.preventDefault();
            ev.stopPropagation();
            const target = ev.target as HTMLDivElement;
            const scroller = scrollRef.current as HTMLDivElement;
            // Делаем скролл
            scroller.scrollTo({ top: index * target.clientHeight, behavior: 'smooth' });

            // И через какое-то время переходим -> эмитируем "нажатие на стрелочку"
            setTimeout(
                () => {
                    onArrowClick(ev, index);
                },
                index * target.clientHeight !== target.scrollTop ? SCROLL_SELECT_ITEM_DELAY : 0,
            );
        };
    };

    if (isMobile && !visible) {
        return null;
    }

    if (orientation === 'right') {
        return (
            <Container>
                <ChooserBox>
                    <ChooserBoxPart style={{ flex: 10 }} >
                        <ChooserOpacity />
                        <ChooserOpacityBottom />
                        <ChooserContainer
                            onScroll={() => {
                                const scroller = scrollRef.current as HTMLDivElement;
                                setScrollingAnimation(Math.floor(scroller.scrollTop) % SCROLL_SELECT_ITEM_HEIGHT !== 0);
                                const index = Math.floor(scroller.scrollTop / SCROLL_SELECT_ITEM_HEIGHT);
                                setProjectIndex(index);
                            }}
                            ref={scrollRef}
                            direction={'column'}
                            directionMobile={'column'}
                            align={'flex-end'}
                            alignMobile={'flex-end'}
                            width={'100%'}
                        >
                            {links.map((value, index) => (
                                <a href={value?.linkTo || ''} title={value?.content + '.' + title}>
                                    <FInter.MainTitleSmallDiv
                                        className={'sc-item'}
                                        onClick={onSomeItemClick(index)}
                                        color={theme.selectContextModal.variantTextColor}
                                        key={index}
                                    >
                                        <ChooserNode style={{ whiteSpace: 'nowrap' }}>{value?.content || ''}</ChooserNode>
                                    </FInter.MainTitleSmallDiv>
                                </a>
                            ))}
                        </ChooserContainer>
                    </ChooserBoxPart>
                    <ChooserBoxPart style={{ flex: 1 }} align={'center'} alignMobile={'center'}>
                        <FlexBox align={'center'} alignMobile={'center'}>
                            <>&nbsp;</>
                            {!scrollingAnimation && <Arrow onClick={(e) => onArrowClick(e)}>→</Arrow>}
                        </FlexBox>
                    </ChooserBoxPart>
                </ChooserBox>
            </Container>
        )
    }

    return (
        <Container>
            <ChooserBox>
                <ChooserBoxPart>
                    <ChooserOpacity />
                    <ChooserOpacityBottom />
                    <ChooserContainer
                        onScroll={(e) => {
                            const scroller = scrollRef.current as HTMLDivElement;
                            setScrollingAnimation(Math.floor(scroller.scrollTop % SCROLL_SELECT_ITEM_HEIGHT) !== 0);
                            const index = Math.floor(scroller.scrollTop / SCROLL_SELECT_ITEM_HEIGHT);
                            setProjectIndex(index);
                        }}
                        ref={scrollRef}
                        direction={'column'}
                        directionMobile={'column'}
                        align={'flex-end'}
                        alignMobile={'flex-end'}
                        width={'100%'}
                    >
                        {links.map((value, index) => {
                            return(
                            <a href={value?.linkTo || ''} title={value?.content + '.' + title}>
                                <FInter.MainTitleSmallDiv
                                    className={'sc-item'}
                                    onClick={onSomeItemClick(index)}
                                    color={theme.selectContextModal.variantTextColor}
                                    key={index}
                                >
                                    <ChooserNode>{value?.content || ''}</ChooserNode>
                                </FInter.MainTitleSmallDiv>
                            </a>
                        )})}
                    </ChooserContainer>
                </ChooserBoxPart>

                <ChooserBoxPart style={{ cursor: 'pointer' }} align={'center'} alignMobile={'center'} onClick={(e) => onArrowClick(e)}>
                    <FInter.MainTitleSmallDiv color={theme.selectContextModal.variantTextColor}>
                        <a href={`${links[projectIndex]?.linkTo}`} title={`${links[projectIndex]?.content}.${title}`}>
                            <FlexBox align={'center'} alignMobile={'center'}>
                                <PrettyPoint visible={!scrollingAnimation}>.</PrettyPoint>
                                <CSpan color={titleColor}>{title}</CSpan>&nbsp;
                                {!scrollingAnimation && <Arrow externalLink={links[projectIndex]?.externalLink}>→</Arrow>}
                            </FlexBox>
                        </a>
                    </FInter.MainTitleSmallDiv>
                </ChooserBoxPart>
            </ChooserBox>
        </Container>
    );
};
// элементы выбора
import React, { FC, useEffect, useRef, useState } from 'react';
import { useTheme } from 'styled-components';
import { CSpan, FInter, FlexBox, useIsMobile } from '@brg/ui';
import { SCROLL_SELECT_ITEM_DELAY, SCROLL_SELECT_ITEM_HEIGHT } from './config';
import {
    Arrow,
    ChooserBox,
    ChooserBoxPart,
    ChooserContainer,
    ChooserNode,
    ChooserOpacity,
    ChooserOpacityBottom,
    Container,
    PrettyPoint,
} from './styles';
import { useModal } from '../../../core';
import { useContextLinksNew } from "../../components/navigationSystem/context/useContextLinksNew";
import { Link } from 'shared/components/link/Link';

/**
 * Контейнер с выбором элемента
 * @param props
 * @constructor
 */
export const SelectContextModalNew: FC<{ visible?: boolean, orientation?: string }> = ({ visible, orientation = 'center' }) => {
    const [scrollingAnimation, setScrollingAnimation] = useState<any>(null);
    const [projectIndex, setProjectIndex] = useState(0);
    const theme = useTheme();
    const modal = useModal();
    const isMobile = useIsMobile();
    const { links, modalSettings: { title, titleColor } } = useContextLinksNew()

    // реф для автоскролла
    const scrollRef = useRef<any>(null);

    // Как только все отрисовалось, надо перевести скролл "куда надо"
    useEffect(() => {
        if (scrollRef.current) {
            const ind = links.findIndex(link => link?.active)
            const scroller = scrollRef.current as HTMLDivElement;
            scroller.scrollTo({ top: ind * SCROLL_SELECT_ITEM_HEIGHT });

            setTimeout(() => {
                setScrollingAnimation(false)
            }, 100)
        }
    }, [links, scrollRef]);

    /**
     * Событие нажатия на стрелочку
     */
    const onArrowClick = (e: React.MouseEvent<HTMLDivElement>, p?: number): void => {
        e.preventDefault();
        e.stopPropagation();
        modal.hide();
        // @ts-ignore
        links[p ?? projectIndex]?.action && links[p ?? projectIndex]?.action()
    };

    /**
     * Фабрика с обработчиком событий
     * @param index
     */
    const onSomeItemClick = (index: number) => {
        /**
         * Обработчик события выбора элемента в списке
         */
        return (ev: React.MouseEvent<HTMLDivElement>): void => {
            ev.preventDefault();
            ev.stopPropagation();
            const target = ev.target as HTMLDivElement;
            const scroller = scrollRef.current as HTMLDivElement;
            // Делаем скролл
            scroller.scrollTo({ top: index * target.clientHeight, behavior: 'smooth' });

            // И через какое-то время переходим -> эмитируем "нажатие на стрелочку"
            setTimeout(
                () => {
                    onArrowClick(ev, index);
                },
                index * target.clientHeight !== target.scrollTop ? SCROLL_SELECT_ITEM_DELAY : 0,
            );
        };
    };

    if (isMobile && !visible) {
        return null;
    }

    if (orientation === 'right') {
        return (
            <Container>
                <ChooserBox>
                    <ChooserBoxPart style={{ flex: 10 }} >
                        <ChooserOpacity />
                        <ChooserOpacityBottom />
                        <ChooserContainer
                            onScroll={() => {
                                const scroller = scrollRef.current as HTMLDivElement;
                                setScrollingAnimation(Math.floor(scroller.scrollTop) % SCROLL_SELECT_ITEM_HEIGHT !== 0);
                                const index = Math.floor(scroller.scrollTop / SCROLL_SELECT_ITEM_HEIGHT);
                                setProjectIndex(index);
                            }}
                            ref={scrollRef}
                            direction={'column'}
                            directionMobile={'column'}
                            align={'flex-end'}
                            alignMobile={'flex-end'}
                            width={'100%'}
                        >
                            {links.map((value, index) => (
                                <a href={value?.linkTo || ''} title={value?.content + '.' + title}>
                                    <FInter.MainTitleSmallDiv
                                        className={'sc-item'}
                                        onClick={onSomeItemClick(index)}
                                        color={theme.selectContextModal.variantTextColor}
                                        key={index}
                                    >
                                        <ChooserNode style={{ whiteSpace: 'nowrap' }}>{value?.content || ''}</ChooserNode>
                                    </FInter.MainTitleSmallDiv>
                                </a>
                            ))}
                        </ChooserContainer>
                    </ChooserBoxPart>
                    <ChooserBoxPart style={{ flex: 1 }} align={'center'} alignMobile={'center'}>
                        <FlexBox align={'center'} alignMobile={'center'}>
                            <>&nbsp;</>
                            {!scrollingAnimation && <Arrow onClick={(e) => onArrowClick(e)}>→</Arrow>}
                        </FlexBox>
                    </ChooserBoxPart>
                </ChooserBox>
            </Container>
        )
    }

    return (
        <Container>
            <ChooserBox>
                <ChooserBoxPart>
                    <ChooserOpacity />
                    <ChooserOpacityBottom />
                    <ChooserContainer
                        onScroll={(e) => {
                            const scroller = scrollRef.current as HTMLDivElement;
                            setScrollingAnimation(Math.floor(scroller.scrollTop % SCROLL_SELECT_ITEM_HEIGHT) !== 0);
                            const index = Math.floor(scroller.scrollTop / SCROLL_SELECT_ITEM_HEIGHT);
                            setProjectIndex(index);
                        }}
                        ref={scrollRef}
                        direction={'column'}
                        directionMobile={'column'}
                        align={'flex-end'}
                        alignMobile={'flex-end'}
                        width={'100%'}
                    >
                        {links.map((value, index) => {
                            return(
                            <a href={value?.linkTo || ''} title={value?.content + '.' + title}>
                                <FInter.MainTitleSmallDiv
                                    className={'sc-item'}
                                    onClick={onSomeItemClick(index)}
                                    color={theme.selectContextModal.variantTextColor}
                                    key={index}
                                >
                                    <ChooserNode>{value?.content || ''}</ChooserNode>
                                </FInter.MainTitleSmallDiv>
                            </a>
                        )})}
                    </ChooserContainer>
                </ChooserBoxPart>

                <ChooserBoxPart style={{ cursor: 'pointer' }} align={'center'} alignMobile={'center'} onClick={(e) => onArrowClick(e)}>
                    <FInter.MainTitleSmallDiv color={theme.selectContextModal.variantTextColor}>
                        <a href={`${links[projectIndex]?.linkTo}`} title={`${links[projectIndex]?.content}.${title}`}>
                            <FlexBox align={'center'} alignMobile={'center'}>
                                <PrettyPoint visible={!scrollingAnimation}>.</PrettyPoint>
                                <CSpan color={titleColor}>{title}</CSpan>&nbsp;
                                {!scrollingAnimation && <Arrow externalLink={links[projectIndex]?.externalLink}>→</Arrow>}
                            </FlexBox>
                        </a>
                    </FInter.MainTitleSmallDiv>
                </ChooserBoxPart>
            </ChooserBox>
        </Container>
    );
};
